<template>
  <div :class="['hold']" role="dialog" aria-modal="true">
    <!-- Fullscreen camera video background -->
    <video
      v-if="config.showCamera"
      ref="cameraVideo"
      class="camera-bg-video"
      autoplay
      muted
      playsinline
    ></video>
    <nav class="nav" role="navigation" aria-label="Navigation">
      <NavBar :showBack="true" @back="onCancel" />
    </nav>
    <div class="verification-center-group" :class="{ 'osk-active': oskActive }">
      <h3 class="verification-title">{{ config.overlay.customText }}</h3>
      <!-- PIN Step -->
      <form v-if="step === 0" class="verification-step" @submit="handleFormSubmit">
        <p class="step-instruction">{{ config.text?.pinInstruction || 'Enter Staff PIN' }}</p>

        <!-- PIN dots (iPhone-style) -->
        <div class="pin-dots" :class="{ shaking: pinErrorShake }" role="group" aria-label="PIN entry">
          <span v-for="n in pinRequiredLength" :key="n" class="pin-dot" :class="{ filled: n <= pin.length }"></span>
        </div>

        <!-- Numeric keypad -->
        <div class="keypad" role="group" aria-label="Numeric keypad">
          <div class="keypad-row">
            <button type="button" class="keypad-btn" @click="onKeypadPress('1')" aria-label="1">1</button>
            <button type="button" class="keypad-btn" @click="onKeypadPress('2')" aria-label="2">2</button>
            <button type="button" class="keypad-btn" @click="onKeypadPress('3')" aria-label="3">3</button>
          </div>
          <div class="keypad-row">
            <button type="button" class="keypad-btn" @click="onKeypadPress('4')" aria-label="4">4</button>
            <button type="button" class="keypad-btn" @click="onKeypadPress('5')" aria-label="5">5</button>
            <button type="button" class="keypad-btn" @click="onKeypadPress('6')" aria-label="6">6</button>
          </div>
          <div class="keypad-row">
            <button type="button" class="keypad-btn" @click="onKeypadPress('7')" aria-label="7">7</button>
            <button type="button" class="keypad-btn" @click="onKeypadPress('8')" aria-label="8">8</button>
            <button type="button" class="keypad-btn" @click="onKeypadPress('9')" aria-label="9">9</button>
          </div>
          <div class="keypad-row">
            <span class="keypad-spacer" aria-hidden="true"></span>
            <button type="button" class="keypad-btn" @click="onKeypadPress('0')" aria-label="0">0</button>
            <button type="button" class="keypad-btn keypad-backspace" @click="onKeypadBackspace" aria-label="Delete">
              <svg width="26" height="20" viewBox="0 0 24 24" class="svg-icon" aria-hidden="true">
                <path fill="currentColor" d="M22 3H7a2 2 0 0 0-1.6.8L1 9l4.4 5.2A2 2 0 0 0 7 15h15a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2m0 10H7l-4-5l4-5h15zM10.5 7.1L9.1 8.5L11.6 11l-2.5 2.5l1.4 1.4L13 12.4l2.5 2.5l1.4-1.4L14.4 11l2.5-2.5l-1.4-1.4L13 9.6z" />
              </svg>
            </button>
          </div>
        </div>

        <div v-if="errorMessage" class="alert alert-danger verification-error" role="alert">
          {{ errorMessage }}
        </div>
        <div v-if="locked" class="alert alert-warning verification-error">
          {{ lockedMessage }}
        </div>
        <button type="submit" class="hidden-submit" style="display: none;"></button>
      </form>
      <!-- DOB Step -->
      <form v-else-if="step === 1" class="verification-step" @submit="handleFormSubmit">
        <p class="step-instruction">{{ config.text?.dobInstruction || 'Enter Date of Birth' }}</p>
        <p v-if="!scanReading" class="scan-instruction">{{ config.text?.scanInstruction || 'Scan ID or enter date of birth' }}</p>
        <span v-if="scanReading" class="scan-reading" role="status" aria-live="polite">
          <span class="scan-spinner" aria-hidden="true"></span>{{ config.text?.scanReading || 'Reading ID…' }}
        </span>
        <span v-else-if="dobSource === 'scan'" class="scan-badge">{{ config.text?.scannedBadge || 'Scanned from ID' }}</span>
        <input
          ref="dobInput"
          class="verification-input"
          :class="{ 'scan-pending': scanReading, 'scan-filled': dobSource === 'scan' && !scanReading }"
          type="tel"
          inputmode="numeric"
          pattern="[0-9]*"
          :placeholder="dobPlaceholder"
          v-model="dob"
          @input="onDobInput"
          @keyup.enter.stop.prevent="onDobSubmit"
          @keydown.enter.stop.prevent="onDobSubmit"
          @keydown.stop="() => {}"
          @focus="handleFocus"
          @blur="handleBlur"
          @change="onDobInputComplete"
          @compositionend="onDobInputComplete"
          :maxlength="dobMaxLength"
          autocomplete="off"
        />
        <div v-if="dobErrorMessage" class="alert alert-danger verification-error" role="alert">
          {{ dobErrorMessage }}
        </div>
        <button type="submit" class="hidden-submit" style="display: none;"></button>
      </form>
      <!-- Only show submit button for DOB step -->
      <footer class="footer" v-if="step === 1">
        <button 
          class="btn btn-lg verification-submit" 
          :class="isDobValid ? 'btn-primary' : 'btn-outline-primary'"
          :disabled="!isDobValid"
          @click="onDobSubmit()"
        >
          {{ config.confirmButtonText }}
        </button>
      </footer>
    </div>
    <!-- ID scan help modal -->
    <div v-if="showScanHelp" class="scan-help-backdrop" role="alertdialog" aria-modal="true" aria-labelledby="scanHelpTitle">
      <div class="scan-help-card">
        <h4 id="scanHelpTitle" class="scan-help-title">{{ config.text?.scanHelpTitle || 'Scan the large barcode' }}</h4>
        <p v-if="scanReading" class="scan-help-message scan-help-reading" role="status" aria-live="polite">
          <span class="scan-spinner" aria-hidden="true"></span>{{ config.text?.scanReading || 'Reading ID…' }}
        </p>
        <p v-else class="scan-help-message">{{ scanHelpMessage }}</p>
        <div class="scan-help-diagram" aria-hidden="true">
          <div class="diagram-card">
            <div class="diagram-cover diagram-cover-top">{{ config.text?.scanHelpCoverLabel || 'Cover' }}</div>
            <div class="diagram-barcode"><span>{{ config.text?.scanHelpScanLabel || 'Scan this' }}</span></div>
            <div class="diagram-cover diagram-cover-bottom">{{ config.text?.scanHelpCoverLabel || 'Cover' }}</div>
          </div>
        </div>
        <p class="scan-help-instruction">
          {{ config.text?.scanHelpInstruction || 'Cover the rest of the card with your hand so only the large barcode on the back faces the scanner, then hold it steady.' }}
        </p>
        <div class="scan-help-actions">
          <button type="button" class="btn btn-lg btn-outline-primary" @click="closeScanHelp(true)">
            {{ config.text?.scanHelpManual || 'Enter Manually' }}
          </button>
          <button type="button" class="btn btn-lg btn-primary" @click="closeScanHelp(false)">
            {{ config.text?.scanHelpRetry || 'Try Again' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import NavBar from '@/components/global/NavBar.vue';
import { isAamvaPayload, parseAamvaDob, formatDobForEntry } from '@/utils/idScanParser';
import { installIdScanGuard, setIdScanConsumer, clearIdScanConsumer } from '@/utils/idScanGuard';

// Presentation scanners re-read a held card roughly every second; stay locked until reads stop
const SCAN_RELEASE_QUIET_MS = 1500;

export default {
  name: 'AgeVerification',
  components: { NavBar },
  props: {
    config: { type: Object, required: true },
    visible: { type: Boolean, default: true }
  },
  emits: ['complete', 'cancel'],
  data() {
    return {
      step: 0, // 0 = PIN, 1 = DOB
      pin: '',
      dob: '',
      errorMessage: '',
      dobErrorMessage: '',
      failedAttempts: 0,
      penaltyDelay: 0,
      penaltyTimeout: null,
      locked: false,
      isFocused: false,
      cameraStream: null,
      pinErrorShake: false,
      dobSource: 'manual', // 'manual' | 'scan'
      dobBeforeScan: '',
      scanLocked: false,
      scanUnlockTimer: null,
      showScanHelp: false,
      scanHelpMessage: '',
      scanReading: false,
      scanConsumer: null
    };
  },
  computed: {
    oskActive() {
      return this.isFocused;
    },
    allowedPinLengths() {
      if (Array.isArray(this.config.allowedPins) && this.config.allowedPins.length > 0) {
        const lengths = Array.from(new Set(this.config.allowedPins.map(p => (p || '').length).filter(Boolean)));
        return lengths.sort((a, b) => a - b);
      }
      return [];
    },
    pinRequiredLength() {
      if (this.allowedPinLengths.length === 1) return this.allowedPinLengths[0];
      if (this.allowedPinLengths.length > 1) return Math.max(...this.allowedPinLengths);
      return 6;
    },
    isPinLengthValid() {
      if (this.config.testMode) return this.pin.length >= 4;
      if (this.allowedPinLengths.length > 0) return this.allowedPinLengths.includes(this.pin.length);
      return this.pin.length >= 4 && this.pin.length <= 6;
    },
    isPinValid() {
      if (!/^\d*$/.test(this.pin)) return false;
      return this.isPinLengthValid;
    },
    isDobValid() {
      if (this.config.testMode) return this.dob.length === this.dobMaxLength;
      return this.validateDob(this.dob);
    },
    dobPlaceholder() {
      switch (this.config.dobEntryFormat) {
        case 'MMDDYY': return 'MMDDYY';
        case 'MMDDYYYY': return 'MMDDYYYY';
        case 'DDMMYY': return 'DDMMYY';
        case 'DDMMYYYY': return 'DDMMYYYY';
        default: return 'MMDDYY';
      }
    },
    dobMaxLength() {
      switch (this.config.dobEntryFormat) {
        case 'MMDDYY': return 6;
        case 'MMDDYYYY': return 8;
        case 'DDMMYY': return 6;
        case 'DDMMYYYY': return 8;
        default: return 6;
      }
    },
    lockedMessage() {
      const template = this.config.text?.tooManyAttempts || 'Too many attempts. Please wait {seconds} seconds.';
      return template.replace('{seconds}', this.penaltyDelay);
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.resetAll();
        if (this.config.showCamera) {
          this.$nextTick(() => {
            this.startCamera();
          });
        }
      } else {
        this.stopCamera();
        this.resetScanState();
      }
    }
  },
  mounted() {
    this.playBeeps();
    this.scanConsumer = {
      onKeyStart: () => { this.dobBeforeScan = this.dob; },
      onScanStart: (meta) => this.onIdScanStart(meta),
      onScanProgress: (raw, meta) => this.onIdScanProgress(raw, meta),
      onScan: (raw, meta) => this.onIdScan(raw, meta)
    };
    installIdScanGuard();
    setIdScanConsumer(this.scanConsumer);
    if (this.config.showCamera) {
      this.$nextTick(() => {
        this.startCamera();
      });
    }
  },
  beforeUnmount() {
    this.stopCamera();
    this.resetScanState();
    if (this.scanConsumer) {
      clearIdScanConsumer(this.scanConsumer);
      this.scanConsumer = null;
    }
  },
  methods: {
    onKeypadPress(digit) {
      if (this.locked) return;
      if (this.pin.length >= this.pinRequiredLength) return;
      this.pin = `${this.pin}${digit}`;
      this.onPinInput();
      // Auto-validate when the entered length matches any allowed length
      if (this.allowedPinLengths.length > 0) {
        if (this.allowedPinLengths.includes(this.pin.length)) {
          setTimeout(() => this.onPinSubmit(), 80);
        }
      } else {
        // Fallback: validate when reaching required length (default behavior)
        if (this.pin.length === this.pinRequiredLength) {
          setTimeout(() => this.onPinSubmit(), 80);
        }
      }
    },
    onKeypadBackspace() {
      if (this.locked) return;
      if (this.pin.length === 0) return;
      this.pin = this.pin.slice(0, -1);
      this.errorMessage = '';
    },
    handleFocus() {
      this.isFocused = true;
    },
    handleBlur() {
      this.isFocused = false;
      // Scanned dates must be confirmed explicitly
      if (this.showScanHelp || this.scanReading || this.dobSource === 'scan') return;
      // Only auto-submit if the input is complete and valid
      // This helps with OSK "Done" button behavior
      if (this.step === 0 && this.isPinValid && this.pin.length >= 4) {
        setTimeout(() => {
          this.onPinSubmit();
        }, 100);
      } else if (this.step === 1 && this.isDobValid && this.dob.length === this.dobMaxLength) {
        setTimeout(() => {
          this.onDobSubmit();
        }, 100);
      }
    },
    handleFormSubmit(event) {
      // Prevent default form submission
      event.preventDefault();
      // Trigger the appropriate submit method based on current step
      if (this.step === 0) {
        this.onPinSubmit();
      } else if (this.step === 1) {
        this.onDobSubmit();
      }
    },
    focusInput() {
      this.$nextTick(() => {
        if (this.step === 1 && this.$refs.dobInput) {
          this.$refs.dobInput.focus();
        }
      });
    },
    onPinInput() {
      this.errorMessage = '';
      if (this.pin.length > this.pinRequiredLength) this.pin = this.pin.slice(0, this.pinRequiredLength);
      if (this.isPinValid && this.config.allowedPins && this.config.allowedPins.includes(this.pin) && !this.locked) {
        setTimeout(() => {
          this.onPinSubmit();
        }, 300);
      }
    },
    onPinInputComplete() {
      // This method is called when the input is complete (e.g., OSK Done button)
      if (this.isPinValid) {
        this.onPinSubmit();
      }
    },
    onPinSubmit() {
      if (!this.isPinValid) {
        this.errorMessage = this.config.text?.invalidPin || 'Enter a valid PIN';
        // Shake and clear for invalid length or non-digit
        this.pinErrorShake = true;
        setTimeout(() => {
          this.pinErrorShake = false;
          this.pin = '';
        }, 250);
        return;
      }
      if (this.config.allowedPins && this.config.allowedPins.includes(this.pin)) {
        this.step = 1;
        this.errorMessage = '';
        this.failedAttempts = 0;
        this.penaltyDelay = 0;
        this.focusInput();
        if (this.penaltyTimeout) {
          clearTimeout(this.penaltyTimeout);
          this.penaltyTimeout = null;
        }
      } else {
        this.failedAttempts++;
        // Trigger wobble and clear PIN for incorrect entry
        this.pinErrorShake = true;
        const clearDelay = 250;
        setTimeout(() => {
          this.pinErrorShake = false;
          this.pin = '';
        }, clearDelay);
        if (this.failedAttempts > 3) {
          this.penaltyDelay = (this.failedAttempts - 3) * 5;
          this.locked = true;
          this.errorMessage = '';
          this.playBeeps(5);
          this.penaltyTimeout = setTimeout(() => {
            this.locked = false;
            this.errorMessage = '';
            this.pin = '';
            this.focusInput();
          }, this.penaltyDelay * 1000);
        } else {
          this.errorMessage = this.config.text?.incorrectPin || 'Incorrect PIN';
          this.playBeeps();
        }
      }
    },
    onDobInput() {
      this.dobErrorMessage = '';
      this.dobSource = 'manual';
      // Allow only digits in DOB input
      this.dob = (this.dob || '').replace(/\D/g, '');
      if (this.dob.length > this.dobMaxLength) this.dob = this.dob.slice(0, this.dobMaxLength);
    },
    onDobInputComplete() {
      // This method is called when the input is complete (e.g., OSK Done button)
      if (this.isDobValid) {
        this.onDobSubmit();
      }
    },
    onDobSubmit() {
      if (!this.isDobValid) {
        if (this.dob.length === 0) {
          this.dobErrorMessage = this.config.text?.enterDob || 'Please enter your date of birth';
        } else if (this.dob.length < this.dobMaxLength) {
          const template = this.config.text?.incompleteDate || 'Please enter complete date ({format})';
          this.dobErrorMessage = template.replace('{format}', this.dobPlaceholder);
        } else if (!this.isValidDate(this.dob)) {
          this.dobErrorMessage = this.config.text?.invalidDate || 'Please enter a valid date';
        } else {
          this.dobErrorMessage = this.config.text?.tooYoung || 'Must be 21 or older to continue';
        }
        return;
      }
      this.$emit('complete', { pin: this.pin, dob: this.dob, dobSource: this.dobSource });
      this.resetAll();
    },
    onIdScanStart({ leaked }) {
      if (this.step !== 1) return;
      // Burst-detected scans let their first few keys reach the input before being recognized
      if (leaked) this.dob = this.dobBeforeScan;
      if (this.scanLocked) return;
      this.scanReading = true;
      this.blurDobInput();
    },
    onIdScanProgress(raw) {
      // Fill as soon as the DOB field has arrived; the rest of the payload keeps streaming in
      if (this.step !== 1 || this.scanLocked || !/DBB\d{8}/.test(raw)) return;
      const parsed = parseAamvaDob(raw);
      if (parsed) this.applyScannedDob(parsed);
    },
    onIdScan(raw, { leaked }) {
      this.scanReading = false;
      if (this.step !== 1) return;
      if (leaked) this.dob = this.dobBeforeScan;
      if (this.scanLocked) {
        this.holdScanLock();
        return;
      }
      const isLicense = isAamvaPayload(raw) || raw.includes('DBB');
      const parsed = isLicense ? parseAamvaDob(raw) : null;
      if (parsed) {
        this.applyScannedDob(parsed);
        return;
      }
      this.scanHelpMessage = isLicense
        ? (this.config.text?.scanFailed || 'Could not read the date of birth from this ID.')
        : (this.config.text?.scanWrongBarcode || 'That is not the ID barcode.');
      this.showScanHelp = true;
      this.blurDobInput();
    },
    applyScannedDob(parsed) {
      this.dob = formatDobForEntry(parsed, this.config.dobEntryFormat);
      this.dobBeforeScan = this.dob;
      this.dobSource = 'scan';
      this.dobErrorMessage = '';
      this.scanReading = false;
      this.showScanHelp = false;
      this.holdScanLock();
      this.blurDobInput();
    },
    blurDobInput() {
      if (this.$refs.dobInput) this.$refs.dobInput.blur();
    },
    holdScanLock() {
      this.scanLocked = true;
      if (this.scanUnlockTimer) clearTimeout(this.scanUnlockTimer);
      this.scanUnlockTimer = setTimeout(() => {
        this.scanLocked = false;
        this.scanUnlockTimer = null;
      }, SCAN_RELEASE_QUIET_MS);
    },
    closeScanHelp(enterManually) {
      this.showScanHelp = false;
      if (enterManually) this.focusInput();
    },
    resetScanState() {
      if (this.scanUnlockTimer) {
        clearTimeout(this.scanUnlockTimer);
        this.scanUnlockTimer = null;
      }
      this.scanLocked = false;
      this.scanReading = false;
      this.showScanHelp = false;
      this.scanHelpMessage = '';
      this.dobBeforeScan = '';
    },
    isValidDate(val) {
      if (!/^[0-9]+$/.test(val)) return false;
      if (val.length !== this.dobMaxLength) return false;
      let month, day, year;
      switch (this.config.dobEntryFormat) {
        case 'MMDDYY':
          month = parseInt(val.substring(0, 2));
          day = parseInt(val.substring(2, 4));
          year = parseInt(val.substring(4, 6));
          year = year <= 29 ? 2000 + year : 1900 + year;
          break;
        case 'MMDDYYYY':
          month = parseInt(val.substring(0, 2));
          day = parseInt(val.substring(2, 4));
          year = parseInt(val.substring(4, 8));
          break;
        case 'DDMMYY':
          day = parseInt(val.substring(0, 2));
          month = parseInt(val.substring(2, 4));
          year = parseInt(val.substring(4, 6));
          year = year <= 29 ? 2000 + year : 1900 + year;
          break;
        case 'DDMMYYYY':
          day = parseInt(val.substring(0, 2));
          month = parseInt(val.substring(2, 4));
          year = parseInt(val.substring(4, 8));
          break;
        default:
          return false;
      }
      if (month < 1 || month > 12) return false;
      if (day < 1 || day > 31) return false;
      const daysInMonth = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
      if (month === 2 && ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0)) {
        daysInMonth[2] = 29;
      }
      if (day > daysInMonth[month]) return false;
      return true;
    },
    validateDob(val) {
      if (!this.isValidDate(val)) return false;
      let month, day, year;
      switch (this.config.dobEntryFormat) {
        case 'MMDDYY':
          month = parseInt(val.substring(0, 2));
          day = parseInt(val.substring(2, 4));
          year = parseInt(val.substring(4, 6));
          year = year <= 29 ? 2000 + year : 1900 + year;
          break;
        case 'MMDDYYYY':
          month = parseInt(val.substring(0, 2));
          day = parseInt(val.substring(2, 4));
          year = parseInt(val.substring(4, 8));
          break;
        case 'DDMMYY':
          day = parseInt(val.substring(0, 2));
          month = parseInt(val.substring(2, 4));
          year = parseInt(val.substring(4, 6));
          year = year <= 29 ? 2000 + year : 1900 + year;
          break;
        case 'DDMMYYYY':
          day = parseInt(val.substring(0, 2));
          month = parseInt(val.substring(2, 4));
          year = parseInt(val.substring(4, 8));
          break;
        default:
          return false;
      }
      const today = new Date();
      const birthDate = new Date(year, month - 1, day);
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) 
        ? age - 1 
        : age;
      return actualAge >= 21;
    },
    resetAll() {
      this.step = 0;
      this.pin = '';
      this.dob = '';
      this.dobSource = 'manual';
      this.errorMessage = '';
      this.dobErrorMessage = '';
      this.resetScanState();
      this.failedAttempts = 0;
      this.penaltyDelay = 0;
      this.locked = false;
      this.isFocused = false;
      if (this.penaltyTimeout) {
        clearTimeout(this.penaltyTimeout);
        this.penaltyTimeout = null;
      }
      this.focusInput();
    },
    playBeeps(beepOverride) {
      if (!this.config.audio || !this.config.audio.enabled) {
        return;
      }
      const beepCount = beepOverride || this.config.audio.beepCount || 3;
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        const ctx = new AudioContext();
        const duration = 0.15;
        const gap = 0.15;
        for (let i = 0; i < beepCount; i++) {
          const oscillator = ctx.createOscillator();
          const gainNode = ctx.createGain();
          oscillator.connect(gainNode);
          gainNode.connect(ctx.destination);
          oscillator.type = 'sine';
          oscillator.frequency.value = 800;
          const startTime = ctx.currentTime + i * (duration + gap);
          oscillator.start(startTime);
          oscillator.stop(startTime + duration);
        }
      } catch (e) {
        console.warn('AudioContext not supported for beeps');
      }
    },
    onCancel() {
      this.$emit('cancel');
      this.resetAll();
    },
    async startCamera() {
      if (!this.config.showCamera || this.cameraStream) return;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            width: { ideal: 640 },
            height: { ideal: 480 },
            facingMode: 'user'
          } 
        });
        this.cameraStream = stream;
        if (this.$refs.cameraVideo) {
          this.$refs.cameraVideo.srcObject = stream;
        }
      } catch (err) {
        console.warn('Camera access denied or not available:', err);
      }
    },
    stopCamera() {
      if (this.cameraStream) {
        this.cameraStream.getTracks().forEach(track => track.stop());
        this.cameraStream = null;
      }
      if (this.$refs.cameraVideo) {
        this.$refs.cameraVideo.srcObject = null;
      }
    }
  }
};
</script>

<style scoped>
.hold {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: fixed;
  z-index: 8000;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  /* Android embedded browser fixes */
  height: 100dvh; /* Dynamic viewport height for modern browsers */
  width: 100dvw; /* Dynamic viewport width for modern browsers */
  padding: env(safe-area-inset-top, 0) env(safe-area-inset-right, 0) env(safe-area-inset-bottom, 0) env(safe-area-inset-left, 0);
  box-sizing: border-box;
  background: v-bind('config.overlay.backgroundColor') !important;
  transition: all 0.3s ease;
  overflow: hidden;
}
.camera-bg-video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  object-fit: cover;
  z-index: 1;
  opacity: 0.5;
  pointer-events: none;
}
.verification-center-group,
.nav {
  position: relative;
  z-index: 2;
}
.verification-center-group {
  height: 100vh;
  height: 100dvh; /* Dynamic viewport height for Android */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 50rem;
  padding: 1.5rem;
  /* Android embedded browser padding fixes */
  padding: max(1.5rem, env(safe-area-inset-top, 1.5rem)) max(1.5rem, env(safe-area-inset-right, 1.5rem)) max(1.5rem, env(safe-area-inset-bottom, 1.5rem)) max(1.5rem, env(safe-area-inset-left, 1.5rem));
  box-sizing: border-box;
  transition: height 0.3s cubic-bezier(0.4,0,0.2,1);
}
.verification-center-group.osk-active {
  height: 32vh;
  margin-bottom: 40vh;
}
.camera-container {
  margin-bottom: 0.5rem;
  z-index: 10;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.camera-osk-mode {
  position: fixed;
  top: 1rem;
  right: 1rem;
  left: auto;
  margin-bottom: 0;
  transform: scale(0.3);
  transform-origin: top right;
  z-index: 1000;
}
.verification-title {
  color: #fff;
  margin-bottom: 0;
  font-size: 2.4rem;
  font-weight: 700;
  text-shadow: 0 4px 20px rgba(0,0,0,0.4);
  text-align: center;
  letter-spacing: -0.02em;
  line-height: 1.2;
}
.input-container {
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
}
.verification-step {
  width: 100%;
  max-width: 42rem;
  margin: 0 auto;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.verification-step form {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.footer {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 0.5rem;
}
.step-instruction {
  color: rgba(255,255,255,0.9);
  font-size: 1.8rem;
  margin-bottom: 2rem;
  font-weight: 500;
  text-shadow: 0 2px 8px rgba(0,0,0,0.3);
  letter-spacing: -0.01em;
}
.scan-instruction {
  color: rgba(255,255,255,0.75);
  font-size: 1.4rem;
  margin-top: -1.25rem;
  margin-bottom: 1.5rem;
  font-weight: 500;
  text-shadow: 0 2px 8px rgba(0,0,0,0.3);
}
.scan-badge {
  display: inline-block;
  margin-bottom: 1rem;
  padding: 0.4rem 1.2rem;
  border-radius: 10rem;
  background: rgba(255,255,255,0.95);
  color: var(--grey-high-emphasis);
  font-size: 1.3rem;
  font-weight: 600;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
}
.scan-reading {
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  margin-bottom: 1rem;
  padding: 0.5rem 1.4rem;
  border-radius: 10rem;
  background: rgba(255,255,255,0.95);
  color: var(--grey-high-emphasis);
  font-size: 1.4rem;
  font-weight: 600;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
}
.scan-spinner {
  width: 1.4rem;
  height: 1.4rem;
  border-radius: 50%;
  border: 3px solid rgba(0,0,0,0.15);
  border-top-color: var(--grey-high-emphasis);
  animation: scan-spin 0.7s linear infinite;
}
.scan-help-reading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
}
.verification-input.scan-pending {
  animation: scan-pulse 1s ease-in-out infinite;
}
.verification-input.scan-filled {
  border-color: #28a745;
  box-shadow: 0 0 0 4px rgba(40,167,69,0.35), 0 8px 32px rgba(0,0,0,0.15);
}
@keyframes scan-spin {
  to { transform: rotate(360deg); }
}
@keyframes scan-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0.6), 0 8px 32px rgba(0,0,0,0.15); }
  50% { box-shadow: 0 0 0 8px rgba(255,255,255,0.15), 0 8px 32px rgba(0,0,0,0.15); }
}
.scan-help-backdrop {
  position: fixed;
  inset: 0;
  z-index: 8200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: rgba(0,0,0,0.6);
  backdrop-filter: blur(6px);
}
.scan-help-card {
  width: 100%;
  max-width: 44rem;
  max-height: 100%;
  overflow-y: auto;
  padding: 2.5rem;
  border-radius: var(--border-radius-xl);
  background: #fff;
  color: var(--grey-high-emphasis);
  text-align: center;
  box-shadow: 0 16px 48px rgba(0,0,0,0.3);
}
.scan-help-title {
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
}
.scan-help-message {
  font-size: 1.6rem;
  font-weight: 500;
  margin-bottom: 1.5rem;
}
.scan-help-instruction {
  font-size: 1.5rem;
  margin: 1.5rem 0 2rem;
}
.scan-help-diagram {
  display: flex;
  justify-content: center;
}
.diagram-card {
  width: 22rem;
  height: 14rem;
  border: 3px solid var(--grey-high-emphasis);
  border-radius: 1.2rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.diagram-cover {
  display: flex;
  align-items: center;
  justify-content: center;
  background: repeating-linear-gradient(45deg, #bbb, #bbb 6px, #999 6px, #999 12px);
  color: #fff;
  font-size: 1.2rem;
  font-weight: 700;
  text-transform: uppercase;
  text-shadow: 0 1px 3px rgba(0,0,0,0.5);
}
.diagram-cover-top {
  height: 22%;
}
.diagram-cover-bottom {
  flex: 1;
}
.diagram-barcode {
  height: 34%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: repeating-linear-gradient(90deg, #111, #111 3px, #fff 3px, #fff 5px, #111 5px, #111 6px, #fff 6px, #fff 9px);
  color: #fff;
  font-size: 1.3rem;
  font-weight: 700;
  outline: 3px solid #28a745;
  outline-offset: -3px;
}
.diagram-barcode span {
  padding: 0.3rem 1rem;
  border-radius: 10rem;
  background: #28a745;
}
.scan-help-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
}
.scan-help-actions .btn {
  min-width: 16rem;
  font-size: 1.6rem;
  padding: 1.2rem 1.5rem;
}
.verification-input {
  width: 100%;
  max-width: 32rem;
  font-size: 2.8rem;
  padding: 2rem 1.5rem;
  text-align: center;
  border: 2px solid rgba(255,255,255,0.2);
  border-radius: var(--border-radius-xl);
  background: rgba(255,255,255,0.95);
  color: var(--grey-high-emphasis);
  font-weight: 500;
  letter-spacing: 0.08em;
  box-shadow: 
    0 8px 32px rgba(0,0,0,0.15),
    inset 0 1px 0 rgba(255,255,255,0.8);
  transition: all 0.3s cubic-bezier(0.4,0,0.2,1);
  backdrop-filter: blur(20px);
  margin-bottom: 2.5rem;
}
.verification-input:focus {
  border-color: rgba(255,255,255,0.6);
  background: rgba(255,255,255,0.98);
  box-shadow: 
    0 16px 48px rgba(0,0,0,0.2),
    0 0 0 3px rgba(255,255,255,0.3),
    inset 0 1px 0 rgba(255,255,255,0.9);
  outline: none;
  transform: translateY(-2px);
}
.verification-input:disabled {
  background: #f5f5f5;
  color: #999;
  border-color: #ddd;
}
.verification-input::placeholder {
  color: #ccc;
  font-weight: 400;
  letter-spacing: 0.25rem;
}
.verification-error {
  font-size: 1.8rem;
  padding: 1.5rem 2rem;
  border-radius: var(--border-radius-lg);
  font-weight: 500;
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.2);
  backdrop-filter: blur(20px);
  color: rgba(255,255,255,0.95);
  box-shadow: 0 8px 32px rgba(0,0,0,0.15);
  margin-top: 1rem;
}
.verification-submit {
  width: 32rem !important;
  font-size: 2rem;
  padding: 2rem 2.5rem;
  border-radius: var(--border-radius-xl);
  font-weight: 600;
  letter-spacing: 0.02em;
  transition: all 0.3s cubic-bezier(0.4,0,0.2,1);
  box-shadow: 
    0 8px 32px rgba(0,0,0,0.15),
    inset 0 1px 0 rgba(255,255,255,0.2);
  backdrop-filter: blur(20px);
  position: relative;
  overflow: hidden;
}
.verification-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0,0,0,0.15);
}
.verification-submit:active:not(:disabled) {
  transform: translateY(0);
}
.verification-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}
/* PIN dots */
.pin-dots {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem 0;
  margin-bottom: 1.5rem;
}
.pin-dots.shaking {
  animation: wobble 350ms ease-in-out;
}
.pin-dot {
  width: 1.4rem;
  height: 1.4rem;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.4);
  background: rgba(255,255,255,0.08);
  display: inline-block;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  transition: all 0.2s cubic-bezier(0.4,0,0.2,1);
  backdrop-filter: blur(8px);
  margin-right: 1.2rem;
}
.pin-dot.filled {
  background: rgba(255,255,255,0.95);
  border-color: rgba(255,255,255,0.95);
  box-shadow: 0 6px 24px rgba(255,255,255,0.2), 0 2px 8px rgba(0,0,0,0.25);
  transform: scale(1.1);
}

.pin-dot:last-child {
  margin-right: 0;
}
/* Numeric keypad */
.keypad {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 28rem;
  margin: 0 auto;
  padding: 0.5rem 0;
}
.keypad-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  justify-items: center;
  margin-bottom: 1rem;
}
.keypad-spacer {
  display: block;
  width: 7.5rem;
}
.keypad-btn {
  width: 7.5rem;
  height: 7.5rem;
  border-radius: 50%;
  border: none;
  background: rgba(255,255,255,0.15);
  backdrop-filter: blur(20px);
  color: rgba(255,255,255,0.95);
  font-size: 2.4rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 
    0 6px 24px rgba(0,0,0,0.15),
    inset 0 1px 0 rgba(255,255,255,0.2),
    inset 0 -1px 0 rgba(0,0,0,0.1);
  transition: all 0.15s cubic-bezier(0.4,0,0.2,1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
}
.keypad-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at center, rgba(255,255,255,0.15) 0%, transparent 70%);
  opacity: 0;
  transition: opacity 0.2s ease;
}
.keypad-btn:hover::before {
  opacity: 1;
}
.keypad-btn:active {
  transform: scale(0.95);
  box-shadow: 
    0 4px 16px rgba(0,0,0,0.2),
    inset 0 2px 8px rgba(0,0,0,0.15);
}
.keypad-backspace {
  background: rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.7);
}
.keypad-backspace:hover {
  background: rgba(255,255,255,0.12);
}
@keyframes wobble {
  0% { transform: translateX(0); }
  15% { transform: translateX(-10px); }
  30% { transform: translateX(8px); }
  45% { transform: translateX(-6px); }
  60% { transform: translateX(4px); }
  75% { transform: translateX(-2px); }
  100% { transform: translateX(0); }
}
/* Camera styles */
.camera-frame {
  position: relative;
  width: 16rem;
  height: 16rem;
  background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
  border-radius: 50%;
  padding: 0.75rem;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.4),
    inset 0 2px 8px rgba(255, 255, 255, 0.1),
    inset 0 -2px 8px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(8px);
  border: 2px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}
.camera-inner {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  overflow: hidden;
  background: #1a1a1a;
  box-shadow: 
    inset 0 4px 12px rgba(0, 0, 0, 0.6),
    inset 0 -4px 12px rgba(0, 0, 0, 0.4);
}
.camera-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  transform: scaleX(-1);
  transition: opacity 0.3s ease;
}
.camera-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  pointer-events: none;
  background: radial-gradient(circle at center, 
    transparent 65%, 
    rgba(0, 0, 0, 0.1) 80%, 
    rgba(0, 0, 0, 0.3) 95%);
}
.camera-status-dot {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 1.2rem;
  height: 1.2rem;
  border-radius: 50%;
  background: rgba(220, 53, 69, 0.8);
  border: 2px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
}
.camera-status-dot.active {
  background: rgba(40, 167, 69, 0.9);
  box-shadow: 
    0 2px 8px rgba(0, 0, 0, 0.3),
    0 0 16px rgba(40, 167, 69, 0.5);
  animation: pulse 2s ease-in-out infinite;
}
@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.8; }
}
.nav {
  position: fixed;
  padding: 2rem;
  top: 0;
  left: 0;
  z-index: 8100;
}
.nav ::v-deep(.btn) {
  color: #fff !important;
  font-weight: 700;
}
.nav ::v-deep(.svg-icon) {
  fill: #fff !important;
  color: #fff !important;
}
.nav ::v-deep(.btn.active) {
  color: rgba(255, 255, 255, 0.8) !important;
}
.nav ::v-deep(.btn.active .svg-icon) {
  fill: rgba(255, 255, 255, 0.8) !important;
}
/* Android embedded browser specific fixes */
@supports (-webkit-touch-callout: none) {
  .hold {
    height: 100vh !important;
    height: -webkit-fill-available !important;
  }
  .verification-center-group {
    height: 100vh !important;
    height: -webkit-fill-available !important;
    padding: 1rem !important;
  }
}

/* General mobile adjustments */
@media (max-width: 480px) {
  .verification-center-group {
    padding: 1rem;
  }
  .verification-title {
    font-size: 2rem;
  }
  .step-instruction {
    font-size: 1.6rem;
  }
  .verification-input {
    font-size: 2.4rem;
    padding: 1.5rem 1rem;
    max-width: 28rem;
  }
  .verification-submit {
    width: 100% !important;
    max-width: 28rem;
    font-size: 1.8rem;
    padding: 1.8rem 2rem;
  }
  .keypad-btn {
    width: 6.5rem;
    height: 6.5rem;
    font-size: 2rem;
  }
  .pin-dot {
    width: 1.2rem;
    height: 1.2rem;
  }
}
@media (max-height: 600px) {
  .hold.keyboard {
    padding-top: 2rem;
  }
  .hold.keyboard .verification-center-group {
    gap: 1rem;
  }
  .hold.keyboard .verification-title {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  .hold.keyboard .step-instruction {
    font-size: 1.6rem;
    margin-bottom: 0.75rem;
  }
  .hold.keyboard .verification-input {
    font-size: 2.5rem;
    padding: 1.25rem;
    margin-bottom: 0.75rem;
  }
  .hold.keyboard .verification-submit {
    width: 28rem !important;
    font-size: 1.8rem;
    padding: 1.25rem 1.5rem;
  }
}
@media (prefers-reduced-motion: reduce) {
  .camera-frame::before {
    animation: none;
  }
  .camera-status-dot.active,
  .scan-spinner,
  .verification-input.scan-pending {
    animation: none;
  }
  .camera-container,
  .camera-frame,
  .camera-video {
    transition: none;
  }
}
</style> 